import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  getAuth,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  User as FirebaseUser,
} from "firebase/auth";
import { app, signInWithGooglePopup } from "../firebase/firebase";
import { request } from "../services/requests";

/* ── types ───────────────────────────────────────────────────────── */

export type AdminRole = "superadmin" | "admin" | "member";

export type PageKey =
  | "sales"
  | "sales_view_user_identity"
  | "withdrawals"
  | "seller_creation"
  | "seller_creation_create"
  | "seller_creation_update"
  | "seller_creation_delete"
  | "registered_users"
  | "notifications";

export interface AdminUser {
  admin_user_id: string;
  email: string;
  display_name: string | null;
  profile_image?: string | null;
  role: AdminRole;
  permissions: PageKey[];
}

interface AuthContextType {
  /** The Firebase user object (null while loading or when logged out) */
  firebaseUser: FirebaseUser | null;
  /** The admin user fetched from the backend (null if not yet loaded) */
  adminUser: AdminUser | null;
  /** True while we are checking auth status on first load */
  loading: boolean;
  /** Error message if login / backend lookup fails */
  error: string | null;
  /** Initiate Google sign-in and verify admin access */
  signIn: () => Promise<void>;
  /** Sign out of Firebase + clear state */
  signOutUser: () => Promise<void>;
  /** Helper: does the current user have access to a given page? */
  hasPermission: (page: PageKey) => boolean;
  /** The current Firebase ID token (for passing to API calls) */
  idToken: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/* ── provider ────────────────────────────────────────────────────── */

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const auth = useMemo(() => getAuth(app), []);

  /**
   * Given a Firebase ID token, call the backend `/auth/me` endpoint
   * to resolve the admin user record (role + permissions).
   */
  const fetchAdminUser = useCallback(async (token: string) => {
    try {
      const res = await request(token).get<AdminUser>("/auth/me");
      setAdminUser(res.data);
      setError(null);
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ??
        "You do not have admin access. Contact a superadmin.";
      setAdminUser(null);
      setError(detail);
    }
  }, []);

  /* Listen for Firebase auth state changes on mount */
  useEffect(() => {
    // Local screenshot/e2e runs only: skip Google and use a fixed token that
    // the test's mocked API accepts. NODE_ENV is "production" in every real
    // build, so this branch is compiled out of what we deploy.
    if (process.env.NODE_ENV !== "production" && process.env.REACT_APP_E2E_AUTH === "1") {
      setFirebaseUser({ uid: "e2e", email: "e2e@inflos.test" } as unknown as FirebaseUser);
      setIdToken("e2e");
      fetchAdminUser("e2e").finally(() => setLoading(false));
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);
      if (user) {
        const token = await user.getIdToken();
        setIdToken(token);
        await fetchAdminUser(token);
      } else {
        setIdToken(null);
        setAdminUser(null);
        setError(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, [auth, fetchAdminUser]);

  // No refresh timer: the request helper asks Firebase for a current token on
  // every call (and retries a 401 with a forced refresh). `idToken` only marks
  // that someone is signed in, so it stays stable and pages don't reload
  // whenever the token rotates.

  const signIn = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const previousUid = auth.currentUser?.uid;
      const result = await signInWithGooglePopup();
      // A new user fires onAuthStateChanged, which loads /auth/me and clears
      // loading. Signing in again as the same user (e.g. after "no admin
      // access") doesn't fire it, so load the admin record here instead.
      if (result.user.uid === previousUid) {
        await fetchAdminUser(await result.user.getIdToken());
        setLoading(false);
      }
    } catch (err: any) {
      setError(err?.message ?? "Sign-in failed.");
      setLoading(false);
    }
  }, [auth, fetchAdminUser]);

  const signOutUser = useCallback(async () => {
    await firebaseSignOut(auth);
    setFirebaseUser(null);
    setAdminUser(null);
    setIdToken(null);
    setError(null);
  }, [auth]);

  const hasPermission = useCallback(
    (page: PageKey): boolean => {
      if (!adminUser) return false;
      if (adminUser.role === "superadmin") return true;
      return (adminUser.permissions ?? []).includes(page);
    },
    [adminUser],
  );

  const value = useMemo<AuthContextType>(
    () => ({
      firebaseUser,
      adminUser,
      loading,
      error,
      signIn,
      signOutUser,
      hasPermission,
      idToken,
    }),
    [
      firebaseUser,
      adminUser,
      loading,
      error,
      signIn,
      signOutUser,
      hasPermission,
      idToken,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/* ── hook ─────────────────────────────────────────────────────────── */

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};
